<template>
  <div>
    <div class="header-block">
      <div class="d-flex justify-content-between">
        <div>Orientačný limit úveru podľa DTI</div>
        <div>
          <v-progress-circular
            v-if="dtiLoading"
            class="loading-spinner extrasmall"
            indeterminate
          ></v-progress-circular>
          <div class="bold" v-else>{{ estimatedDti?.value | dotTheAmount }} EUR</div>
        </div>
      </div>
      <div class="d-flex justify-content-between">
        <div>Akceptovaný príjem / mes.</div>
        <div>
          <v-progress-circular
            v-if="dtiLoading"
            class="loading-spinner extrasmall"
            indeterminate
          ></v-progress-circular>
          <div class="bold" v-else>{{ estimatedDti?.extra?.prijem_mo | dotTheAmount }} EUR</div>
        </div>
      </div>
      <div v-if="income?.error">
        <br>
        <div
          v-for="(error, key) in income.error"
          :key="key"
          class="error-text"
        >
          {{ error }}
        </div>
      </div>
    </div>
    <form @submit.prevent="saveIncome" id="income-submit">
      <div class="income-methodology-note">
        Dôchodok sa uloží ako doplnkový príjem. Jeho akceptovanú výšku vypočítame pre každú banku samostatne.
      </div>
      <div class="dropdown-input-custom rest-input" style="margin-top: 20px">
        <dropdown-select
          @select="setDropdownValue($event, 'druh')"
          :id="'retirement_druh'"
          :init-value="form.druh"
          :options="druhChoices"
          normal
          :name="'Typ dôchodku'"
        />
      </div>
      <div class="input-group">
        <label for="vyska-dochodok" class="field-label"
        >Výška dôchodku<strong></strong
        ></label>
        <input
          @change="showEstimatedDtiToUser"
          v-model.number="form.suma"
          type="number"
          step="0.01"
          min="0"
          class="text-input w-input required"
          name="vyska-dochodok"
          data-name="vyska-dochodok"
          required
          id="vyska-dochodok"
        />
      </div>
      <div v-if="isInvalidPension" class="input-group">
        <label for="invalidity-percent" class="field-label">
          Pokles schopnosti pracovať v percentách
        </label>
        <input
          @change="showEstimatedDtiToUser"
          v-model.number="form.invalidityPercent"
          type="number"
          step="0.01"
          min="0"
          max="100"
          class="text-input w-input required"
          name="invalidity-percent"
          required
          id="invalidity-percent"
        />
        <small class="field-help">Údaj je potrebný na presné posúdenie invalidného dôchodku v UniCredit Bank.</small>
      </div>
    </form>
  </div>
</template>

<script>
import DropdownSelect from "../../../DropdownSelect";
import { getRestIncomeDTI } from "@/services/brokerService";

export default {
  name: "RetirementPension",
  components: {DropdownSelect},
  props: {
    income: [Object, null],
    formTemplate: Object,
    clientInfo: [Object, null],
  },
  computed: {
    isInvalidPension () {
      return this.form.druh === "invalidny_dochodca_v_sr";
    },
  },
  created () {
    if (this.income) {
      this.form.suma = this.income.suma;
      this.form.druh = this.income.druh;
      this.form.invalidityPercent = this.income.meta?.pokles_schopnosti_pracovat_percent ?? null;
    }
  },
  data: () => ({
    dtiLoading: false,
    form: {
      druh: null,
      suma: 0,
      invalidityPercent: null,
    },
    estimatedDti: null,
    druhChoices: [
      {
        display_name: "výsluhový",
        value: "vysluhovy_dochodca",
      },
      {
        display_name: "starobný",
        value: "starobny_dochodca",
      },
      {
        display_name: "vdovský",
        value: "vdovsky_dochodca",
      },
      {
        display_name: "invalidný",
        value: "invalidny_dochodca_v_sr",
      },
    ],
  }),
  methods: {
    async showEstimatedDtiToUser () {
      this.dtiLoading = true;
      const incomeData = {
        suma: this.form.suma,
        druh: this.form.druh,
        typ: "vedlajsi",
        vek: this.clientInfo?.age ?? null,
        meta: this.isInvalidPension
          ? {pokles_schopnosti_pracovat_percent: this.form.invalidityPercent}
          : {},
      };
      this.estimatedDti = null;
      const invalidityDataComplete = !this.isInvalidPension || this.checkValue(this.form.invalidityPercent);
      if (this.checkValue(incomeData.suma) && this.checkValue(incomeData.druh) && invalidityDataComplete) {
        const dtiData = await getRestIncomeDTI(incomeData);
        this.pickHighestDti(dtiData);
      }
      this.dtiLoading = false;
    },
    checkValue(value) {
      return !(value === undefined || value === null || value === "");
    },
    pickHighestDti (dtiInfo) {
      const successfulResults = Object.values(dtiInfo || {})
        .filter((result) => result?.success && Number.isFinite(Number(result.value)));
      if (!successfulResults.length) return;
      this.estimatedDti = successfulResults.reduce((highest, result) => (
        Number(result.value) > Number(highest.value) ? result : highest
      ));
    },
    saveIncome () {
      const incomeObject = {
        typ: "vedlajsi",
        druh: this.form.druh,
        suma: this.form.suma,
        meta: {
          ...(this.income?.meta || {}),
          ...(this.isInvalidPension
            ? {pokles_schopnosti_pracovat_percent: this.form.invalidityPercent}
            : {}),
        },
      };
      if (!this.isInvalidPension) {
        delete incomeObject.meta.pokles_schopnosti_pracovat_percent;
      }
      if (this.income) {
        incomeObject.id = this.income.id;
      }
      this.$emit("income-saved", {incomeInfo: incomeObject});
    },
    setDropdownValue (value, dropdownName) {
      this.form[dropdownName] = value;
      this.showEstimatedDtiToUser();
    },
  },
};
</script>

<style scoped lang="scss">
.income-methodology-note {
  margin-top: 20px;
  padding: 16px 18px;
  border: 1px solid rgba(93, 174, 241, 0.35);
  border-radius: 16px;
  color: var(--theme-text-secondary, #66738f);
  line-height: 1.45;
}

.field-help {
  display: block;
  margin-top: 8px;
  color: var(--theme-text-secondary, #66738f);
  line-height: 1.4;
}
</style>
